import {Component, h, Prop, State} from '@stencil/core';
import {Cart, SplitOption, StepItem, Translations} from '../../../redux/types';
import {AmountToPay, Price} from '../../../types/types';
import {store} from "../../../redux/store";
import {RootState} from "../../../redux/reducers";
import {Unsubscribe} from "@stencil/redux";
import {trans} from "../../../utils/trans";

@Component({
    tag: 'rentware-cart-control-button-checkout',
    styleUrl: 'style.sass',
    shadow: true,
})
export class RentwareCartControlButtonCheckout {
    @Prop() onClick;
    @Prop() total: number;
    @Prop() price: Price;
    @Prop() depositPrice: Price;
    @Prop() isAlreadyOpened = false;
    @Prop() splitOption: SplitOption;
    @Prop({mutable: true}) currentStep: StepItem;
    @Prop({mutable: true}) paymentSplitOption: SplitOption;
    @Prop({mutable: true}) locale: string;
    @Prop({mutable: true}) translations: Translations;
    @Prop({mutable: true}) cart: Cart;

    @State() totalState: number = 0;
    @State() totalStyle = "Summary";

    @Prop({mutable: true}) amountToPay: AmountToPay;
    unsubscribe!: Unsubscribe;

    componentWillLoad() {
        this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
            return {
                amountToPay: state.app.amountToPay,
                currentStep: state.app.currentStep,
                paymentSplitOption: state.app.paymentSplitOption,
                locale: state.app.locale,
                translations: state.system.translations,
                cart: state.cart,
            };
        });
    }

    disconnectedCallback() {
        this.unsubscribe();
    }

    componentWillRender() {
        if (this.total !== undefined && this.total !== this.totalState) {
            this.totalState = this.total;
            this.totalStyle = "Summary";
            setTimeout(() => {
                this.totalStyle = "Summary Animated";
            }, 0)
        }
    }

    get iconOpened() {
        if (this.isAlreadyOpened) {
            return (
                <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 0.5L5 4.5L1 0.499999" stroke="#46435B" stroke-linecap="round"
                          stroke-linejoin="round"/>
                </svg>
            )
        } else {
            return (
                <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 4.5L5 0.5L9 4.5" stroke="#46435B" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            )
        }
    }

    get priceWithSplit() {
        if (this.isPaymentStep) {
            return {gross: this.amountToPay?.total, net: this.amountToPay?.total, vat: 0}
        }

        return this.price;
    }

    get isPaymentStep() {
        return this.currentStep?.kind === 'payment';
    }

    get totalText() {
        if (this.isPaymentStep) {
            return trans(
                'cart.payAmount',
                this.locale,
                this.translations,
            )
        }

        return trans(
            'cart.totalArticles',
            this.locale,
            this.translations,
            {quantity: this.cart?.totalQuantityWithShipping ?? 0}
        )
    }

    get totalPrice() {
        if (!this.amountToPay) {
            return null;
        }

        const payingFullPrice = this.amountToPay?.total === this.amountToPay?.withSubtractedGiftCard;
        if (this.isPaymentStep && !payingFullPrice) {
            return (
                <span class="Price">
                    <rentware-total-price
                        price={{gross: this.amountToPay?.withSubtractedGiftCard, net: this.amountToPay?.withSubtractedGiftCard, vat: 0}}
                        showTotalText={false}
                    />
                    <span class="SubtlePrice">
                        &nbsp;({trans('cart.payOfTotal', this.locale, this.translations)}&nbsp;
                        <rentware-total-price
                            price={this.priceWithSplit}
                            showTotalText={false}
                        />
                        )
                    </span>
                </span>
            );
        }

        return (
            <span class="Price">
                <rentware-total-price
                    price={this.priceWithSplit}
                    showTotalText={!this.isPaymentStep}
                />
            </span>
        )
    }

    renderDepositPrice() {
      if (this.depositPrice?.gross > 0) {
        return (
          <span style={{fontSize: "10px"}}>
            <rentware-deposit-price price={this.depositPrice} />
          </span>
        )
      }

      return null
    }

    render() {
        return (
            <div
                class="CheckoutContainer"
                onClick={this.onClick}
                tabIndex={0}
            >
                <svg width="26" height="23" viewBox="0 0 26 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M1.27951 0.440983C0.970772 0.286615 0.595351 0.411755 0.440983 0.720491C0.286615 1.02923 0.411755 1.40465 0.720491 1.55902L1.27951 0.440983ZM5.4 19.15H4.775V19.775H5.4V19.15ZM23.0643 20.9854C23.0908 21.3296 23.3913 21.5871 23.7354 21.5607C24.0796 21.5342 24.3371 21.2337 24.3107 20.8896L23.0643 20.9854ZM23.621 20.0733L22.9979 20.1212L23.621 20.0733ZM0.720491 1.55902L3.46213 2.92984L4.02115 1.8118L1.27951 0.440983L0.720491 1.55902ZM4.775 5.0541V19.15H6.025V5.0541H4.775ZM5.4 19.775H22.624V18.525H5.4V19.775ZM22.9979 20.1212L23.0643 20.9854L24.3107 20.8896L24.2442 20.0254L22.9979 20.1212ZM22.624 19.775C22.8199 19.775 22.9828 19.9259 22.9979 20.1212L24.2442 20.0254C24.1791 19.1787 23.4731 18.525 22.624 18.525V19.775ZM3.46213 2.92984C4.26674 3.33214 4.775 4.15452 4.775 5.0541H6.025C6.025 3.68105 5.24924 2.42585 4.02115 1.8118L3.46213 2.92984Z"
                        fill="#46435B"/>
                    <circle cx="5.39973" cy="19.6999" r="2.675" stroke-width="1.25"/>
                    <circle cx="22.9998" cy="20.8001" r="1.575" stroke-width="1.25"/>
                    <rect x="11.5253" y="11.5249" width="7.55" height="7.55" stroke-width="1.25"/>
                    <path d="M15.2998 12V14.75" stroke-width="1.25" stroke-linecap="round"/>
                </svg>

                <div class="ShowArticleContainer">
                    <div class={this.totalStyle}>
                        {this.totalText}
                        {this.totalPrice}
                        {this.renderDepositPrice()}
                    </div>

                    {this.iconOpened}
                </div>
            </div>
        )
    }
}
